import React, { FC, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from 'styled-components';
import { useTranslate } from '@/translations';
import { useThroughputTrend } from '@/api/OperationsDashboard';
import { ChartCard } from './ChartCard';

const HEIGHT = 300;

export const ThroughputTrendChart: FC = () => {
  const { t } = useTranslate();
  const { primaryChartColor, secondaryChartColor, accent } = useTheme();
  const { data, isLoading, isError, refetch } = useThroughputTrend();

  const arrivals = t('dashboard.trend.arrivals');
  const despatches = t('dashboard.trend.despatches');
  const stock = t('dashboard.trend.stock');

  const options: echarts.EChartsOption = useMemo(
    () => ({
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
      legend: { data: [arrivals, despatches, stock], bottom: 0 },
      grid: { left: 48, right: 56, top: 16, bottom: 44 },
      xAxis: { type: 'category', data: (data ?? []).map((p) => p.weekLabel) },
      yAxis: [{ type: 'value' }, { type: 'value', splitLine: { show: false } }],
      series: [
        {
          name: arrivals,
          type: 'bar',
          data: (data ?? []).map((p) => p.arrivals),
          itemStyle: { color: primaryChartColor },
        },
        {
          name: despatches,
          type: 'bar',
          data: (data ?? []).map((p) => p.despatches),
          itemStyle: { color: secondaryChartColor },
        },
        {
          name: stock,
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          showSymbol: false,
          data: (data ?? []).map((p) => p.stockEnd),
          lineStyle: { color: accent, width: 2 },
          itemStyle: { color: accent },
        },
      ],
    }),
    [
      data,
      primaryChartColor,
      secondaryChartColor,
      accent,
      arrivals,
      despatches,
      stock,
    ],
  );

  return (
    <ChartCard
      title={t('dashboard.widget.trend')}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      height={HEIGHT}
    >
      <ReactECharts option={options} style={{ height: HEIGHT }} />
    </ChartCard>
  );
};
